import React, { useState, useEffect, useRef } from 'react';
import { I, C } from './icones';
import VoletCompact from './VoletCompact';
import { libelleMenu } from '../../i18n/menu';

/* ── Sous-menu animé ── */
const SubMenu = ({ children, open }) => {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  useEffect(() => { if (ref.current) setH(open ? ref.current.scrollHeight : 0); }, [open]);
  return (
    <div style={{ maxHeight: h, overflow: 'hidden', transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1)' }}>
      <div ref={ref}>{children}</div>
    </div>
  );
};

export default function Item({ item, sub = false, openKeys, toggleKey, isActive, navigate,
  nonLusMail = 0, nonLusConversations = 0, demandesAppareils = 0, declarationsCorbeille = 0,
  compact = false, refermerGroupe }) {
  const [hovered, setHovered] = useState(false);
  const hasChildren = item.children?.length > 0;
  // En mode compact, seuls les items racines sont rendus : un conteneur
  // (path null) doit donc s'allumer des qu'un de ses enfants correspond a la
  // route active, sinon aucune icone ne s'allume jamais pour ces routes. En
  // mode etendu, l'enfant deplie s'allume deja individuellement - le parent
  // ne doit pas s'allumer en meme temps, d'ou la restriction a `compact`.
  const activeViaEnfant = compact && hasChildren && item.children.some((ch) => isActive(ch.path));
  const active = isActive(item.path) || activeViaEnfant;
  const isOpen = openKeys.includes(item.cle);
  const Icon = I[item.icone];

  const handleClick = () => {
    if (hasChildren) {
      toggleKey(item.cle);
      return;
    }
    navigate(item.path);
    // Le groupe se referme de lui-meme : une fois l'entree choisie, ses voisines
    // n'ont plus d'utilite et occupent la hauteur de barre. C'est a la navigation
    // de ranger derriere elle, pas a l'utilisateur d'aller replier a la main.
    // `refermerGroupe` n'existe que pour les sous-entrees - une entree racine
    // n'appartient a aucun groupe et n'a donc rien a refermer.
    if (sub && typeof refermerGroupe === 'function') refermerGroupe();
  };

  const ancreRef = useRef(null);
  const [volet, setVolet] = useState(null);
  // Distingue une ouverture au clavier (Entree/Espace) d'une ouverture a la
  // souris ou au simple passage du focus (Tab) : seule la premiere doit
  // voler le focus vers la premiere ligne du volet.
  const [ouvertParClavier, setOuvertParClavier] = useState(false);
  const minuteurFermeture = useRef(null);
  // Drapeau pose juste avant le .focus() programmatique de fermerVoletClavier :
  // cette ancre porte onFocus={ouvrirVolet}, donc rendre le focus a l'ancre
  // declenche synchroniquement une reouverture si on ne l'empeche pas. Le
  // drapeau n'est arme que lorsque le focus n'est pas deja sur l'ancre (sinon
  // .focus() est un no-op, aucun onFocus ne se declenche, et le drapeau
  // resterait arme a tort pour le prochain vrai passage de focus).
  const ignorerProchainFocus = useRef(false);

  const annulerFermeture = () => {
    if (minuteurFermeture.current) {
      clearTimeout(minuteurFermeture.current);
      minuteurFermeture.current = null;
    }
  };

  const ouvrirVolet = () => {
    if (ignorerProchainFocus.current) {
      ignorerProchainFocus.current = false;
      return;
    }
    annulerFermeture();
    setOuvertParClavier(false);
    if (ancreRef.current) setVolet(ancreRef.current.getBoundingClientRect());
  };

  const ouvrirVoletClavier = () => {
    annulerFermeture();
    setOuvertParClavier(true);
    if (ancreRef.current) setVolet(ancreRef.current.getBoundingClientRect());
  };

  // Fermeture DIFFEREE, jamais immediate : la souris doit pouvoir traverser
  // l'espace entre l'icone et le volet sans que celui-ci disparaisse.
  const fermerVolet = () => {
    annulerFermeture();
    minuteurFermeture.current = setTimeout(() => {
      setVolet(null);
      setOuvertParClavier(false);
    }, 150);
  };

  // Fermeture IMMEDIATE, utilisee par Echap depuis le volet : rend aussitot
  // le focus a l'icone d'origine, sans attendre le delai de 150 ms prevu
  // pour la souris.
  const fermerVoletClavier = () => {
    annulerFermeture();
    setVolet(null);
    setOuvertParClavier(false);
    if (document.activeElement !== ancreRef.current) {
      ignorerProchainFocus.current = true;
    }
    ancreRef.current?.focus();
  };

  const gererClavierAncre = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (hasChildren) ouvrirVoletClavier();
      else if (item.path) navigate(item.path);
    } else if (e.key === 'Escape' && volet) {
      e.preventDefault();
      fermerVoletClavier();
    }
  };

  // Evite un setState apres demontage si l'entree disparait volet ouvert
  // (changement d'habilitations, rechargement du menu).
  useEffect(() => annulerFermeture, []);

  if (compact) {
    // En mode compact, un clic navigue si l'entree a un chemin ; sinon le
    // volet est le seul acces aux enfants - d'ou son ouverture au focus
    // clavier autant qu'au survol.
    return (
      <>
        <div
          ref={ancreRef}
          tabIndex={0}
          role="button"
          aria-label={libelleMenu(item)}
          aria-haspopup={hasChildren ? 'menu' : undefined}
          aria-expanded={hasChildren ? !!volet : undefined}
          onMouseEnter={ouvrirVolet}
          onMouseLeave={fermerVolet}
          onFocus={ouvrirVolet}
          onBlur={fermerVolet}
          onClick={() => { if (item.path) navigate(item.path); }}
          onKeyDown={gererClavierAncre}
          title={libelleMenu(item)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: 40, borderRadius: 8, marginBottom: 2, cursor: 'pointer',
            background: active ? C.active : 'transparent',
            color: active ? '#fff' : C.textSec, position: 'relative',
          }}
        >
          {Icon ? <Icon /> : <I.Dot />}
          {item.badge === 'alertes' && (
            <span style={{
              position: 'absolute', top: 6, right: 10, width: 7, height: 7,
              borderRadius: '50%', background: '#ef4444',
            }} />
          )}
          {item.badge === 'mail' && nonLusMail > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 6, background: '#1a6b3c',
              color: '#fff', borderRadius: 9, fontSize: 9, fontWeight: 700,
              padding: '0 4px', minWidth: 14, textAlign: 'center',
            }}>{nonLusMail > 99 ? '99+' : nonLusMail}</span>
          )}
          {item.badge === 'conversations' && nonLusConversations > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 6, background: '#ef4444',
              color: '#fff', borderRadius: 9, fontSize: 9, fontWeight: 700,
              padding: '0 4px', minWidth: 14, textAlign: 'center',
            }}>{nonLusConversations > 99 ? '99+' : nonLusConversations}</span>
          )}
          {/* Ambre et non rouge : une demande en attente appelle une décision,
              elle ne signale pas un incident. Le rouge est déjà pris par les
              messages non lus, et deux rouges côte à côte ne hiérarchisent rien. */}
          {item.badge === 'appareils' && demandesAppareils > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 6, background: '#f59e0b',
              color: '#fff', borderRadius: 9, fontSize: 9, fontWeight: 700,
              padding: '0 4px', minWidth: 14, textAlign: 'center',
            }}>{demandesAppareils > 99 ? '99+' : demandesAppareils}</span>
          )}
          {/* Meme ambre que les appareils, et pour la meme raison : une declaration
              qui attend appelle une prise en charge, elle ne signale pas un incident.
              Le rouge reste reserve aux messages non lus. */}
          {item.badge === 'declarations' && declarationsCorbeille > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 6, background: '#f59e0b',
              color: '#fff', borderRadius: 9, fontSize: 9, fontWeight: 700,
              padding: '0 4px', minWidth: 14, textAlign: 'center',
            }}>{declarationsCorbeille > 99 ? '99+' : declarationsCorbeille}</span>
          )}
        </div>
        <VoletCompact
          ancre={volet} item={item} navigate={navigate}
          onEntrer={annulerFermeture}
          onFermer={fermerVolet}
          onEchap={fermerVoletClavier}
          focaliserPremiereLigne={ouvertParClavier}
        />
      </>
    );
  }

  return (
    <div>
      <div
        role="link"
        tabIndex={0}
        // Sur un lien, `aria-current="page"` est ce qui annonce « vous etes ici »
        // a un lecteur d'ecran. Le fond plus clair ne dit rien a qui ne voit pas.
        aria-current={active && item.path ? 'page' : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center',
          gap: sub ? 8 : 10,
          padding: sub ? '7px 12px 7px 33px' : '9px 12px',
          // Decrochage sous l'en-tete de section, devenu une carte lui aussi :
          // deux cartes de meme largeur ne diraient plus laquelle contient l'autre.
          marginLeft: sub ? 0 : 10,
          borderRadius: 10, cursor: 'pointer', marginBottom: 3,
          // Chaque entree est une CARTE : un bloc arrondi plein largeur, lisible
          // comme un objet a part entiere. L'etat actif se lit a l'intensite du
          // fond, pas a un liseré lateral - une barre d'accent tranche sur le
          // vert et attire l'oeil plus que le libelle qu'elle designe.
          background: active
            ? 'rgba(255,255,255,0.18)'
            : hovered ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)',
          transition: 'background 0.15s',
        }}
      >
        {!sub && Icon && (
          <span style={{
            width: 28, height: 28, borderRadius: 7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)',
            color: active ? '#fff' : C.textSec, flexShrink: 0,
          }}>
            <Icon />
          </span>
        )}
        {sub && (
          <span style={{ color: active ? '#fff' : C.textMut, flexShrink: 0, paddingTop: 1 }}>
            <I.Dot />
          </span>
        )}
        <span style={{
          flex: 1, fontSize: sub ? 14.5 : 15.5,
          fontWeight: active ? 650 : 400,
          // rgba(255,255,255,.82) sur le vert #1a6b3c donne un contraste > 4.5:1
          // (exigence AA). Le blanc plein sur tous les items rendait l'actif et
          // l'inactif indistinguables.
          color: active ? '#fff' : 'rgba(255,255,255,0.82)',
          lineHeight: 1.3,
        }}>
          {libelleMenu(item)}
        </span>
        {item.badge === 'alertes' && (
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 10,
            fontSize: 10, fontWeight: 700, padding: '1px 6px',
          }}>!</span>
        )}
        {item.badge === 'mail' && nonLusMail > 0 && (
          <span style={{
            background: '#1a6b3c', color: '#fff', borderRadius: 10,
            fontSize: 10, fontWeight: 700, padding: '1px 7px', minWidth: 18,
            textAlign: 'center',
          }}>{nonLusMail > 99 ? '99+' : nonLusMail}</span>
        )}
        {item.badge === 'conversations' && nonLusConversations > 0 && (
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 10,
            fontSize: 10, fontWeight: 700, padding: '1px 7px', minWidth: 18,
            textAlign: 'center',
          }}>{nonLusConversations > 99 ? '99+' : nonLusConversations}</span>
        )}
        {item.badge === 'appareils' && demandesAppareils > 0 && (
          <span style={{
            background: '#f59e0b', color: '#fff', borderRadius: 10,
            fontSize: 10, fontWeight: 700, padding: '1px 7px', minWidth: 18,
            textAlign: 'center',
          }}>{demandesAppareils > 99 ? '99+' : demandesAppareils}</span>
        )}
        {item.badge === 'declarations' && declarationsCorbeille > 0 && (
          <span style={{
            background: '#f59e0b', color: '#fff', borderRadius: 10,
            fontSize: 10, fontWeight: 700, padding: '1px 7px', minWidth: 18,
            textAlign: 'center',
          }}>{declarationsCorbeille > 99 ? '99+' : declarationsCorbeille}</span>
        )}
        {hasChildren && (
          <span style={{
            color: C.textMut, transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s', display: 'flex',
          }}>
            <I.Chevron />
          </span>
        )}
      </div>
      {hasChildren && (
        <SubMenu open={isOpen}>
          {item.children.map((ch) => (
            <Item key={ch.cle} item={ch} sub
              openKeys={openKeys} toggleKey={toggleKey} isActive={isActive} navigate={navigate}
              refermerGroupe={() => toggleKey(item.cle)} />
          ))}
        </SubMenu>
      )}
    </div>
  );
}
